"use client";

import ballImage from "@/assets/global/ball.webp";
import { noBtnCls, shadowCls, yesBtnCls } from "@/components/platform/style";
import VolRect from "@/components/platform/vol-rect";
import { CricketMatchRecordDisplay } from "@/components/sport-event/cricket-board/cricket-match-record-display";
import { useTradePanel } from "@/components/trade-panel/hooks/use-trade-panel";
import { OrderType } from "@/components/trade-panel/types";
import { SmartImage } from "@/components/ui/smart-image";
import { type DOMInfo, useCollectDOMInfo } from "@/hooks/useCollectDOMInfo";
import { cn } from "@/lib/utils";
import { isEventExpired, isGuessExpired } from "@/utils/expiration";
import { formatCentPriceAsChancePercent } from "@/utils/price";
import { navigateToEventDetail, skipRoutes, useAppRouter } from "@/utils/router";
import { formatUnitPrice, get, getOptionLabel } from "@/utils/utils";
import { EventCategory } from "@yn/utils";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import { usePathname } from "next/navigation";
import { forwardRef, useImperativeHandle, useMemo, useState } from "react";
import PredictionCardContainer from "../sport-event/PredictionCardContainer";
import { resolveSportEventCardState } from "./sport-event-card-state";
import { getSportIcon } from "./sport-icons";

dayjs.extend( duration );

const DEFAULT_OPTION_PRICE = 50;
type OptionPriceKey = "yesRealtimePrice" | "noRealtimePrice";

function getOptionPrice( guess: any, key: OptionPriceKey ) {
    return get( guess, key, DEFAULT_OPTION_PRICE ) as number | string;
}

function getOptionPercentText( guess: any, key: OptionPriceKey ) {
    return formatCentPriceAsChancePercent( getOptionPrice( guess, key ) );
}

function isCricketEventInfo( eventInfo: any ) {
    return Number( get( eventInfo, "eventCategory", EventCategory.Normal ) ) === EventCategory.Cricket
        || eventInfo.ifCricket === true;
}

function getSportCardIcon( eventInfo: any ) {
    const eventCategory = Number( get( eventInfo, "eventCategory", EventCategory.Normal ) );
    return getSportIcon( isCricketEventInfo( eventInfo ) ? EventCategory.Cricket : eventCategory as EventCategory )
        || get( eventInfo, "eventImageUrl", "" )
        || get( eventInfo, "imageUrl", "" )
        || ballImage;
}

// SportSimpleEventCard 组件的 ref 类型定义
export interface SportSimpleEventCardRef {
    collectDOMInfo: () => DOMInfo;
}

interface SportSimpleEventCardProps {
    eventInfo: any;
    className?: string;
    imageSize?: "default" | "large"; // Image size: default (40x40) or large (56x56)
    orderType?: OrderType;
    onCardClick?: () => void; // Card click event
}

interface PromotedEventSectionProps {
    eventInfo: any;
    imageSize: "default" | "large";
    isEnded: boolean;
    yesDisplayText: string;
    noDisplayText: string;
    onToggleYes: () => void;
    onToggleNo: () => void;
}

// 主推事件态：展示事件头部和主竞猜对应的对阵双方信息。
function PromotedEventSection(
    { eventInfo, imageSize, isEnded, yesDisplayText, noDisplayText, onToggleYes, onToggleNo }:
        PromotedEventSectionProps,
) {
    const isCricketEvent = isCricketEventInfo( eventInfo );
    const eventImage = get( eventInfo, "eventImageUrl", "" ) || get( eventInfo, "imageUrl", "" );
    const sportIcon = getSportCardIcon( eventInfo );
    const title = get( eventInfo, "eventTitle", "" ) || get( eventInfo, "title", "" );

    return (
        <>
            <div className="flex items-center gap-3 mb-2">
                {( eventImage || isCricketEvent ) && (
                    <SmartImage
                        src={eventImage || sportIcon}
                        alt=""
                        className={cn( "flex-shrink-0", eventImage ? "rounded-sm object-cover size-[38px]" : "size-6" )}
                    />
                )}
                <span className="text-sm font-semibold text-t1 line-clamp-3">{title}</span>
            </div>

            <div className="mb-2">
                <div className="flex items-center mb-2 gap-2 h-8">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                        {get( eventInfo, "matchups.0.matchupImageUrl", "" ) && (
                            <SmartImage
                                src={get( eventInfo, "matchups.0.matchupImageUrl", "" )}
                                alt=""
                                className="size-8 rounded-lg object-cover flex-shrink-0"
                                imageUrlOptions={{ width: 150, height: 150 }}
                            />
                        )}
                        <span className="text-sm font-medium text-t1 truncate flex-1 min-w-0 flex items-center gap-1">
                            {get( eventInfo, "matchups.0.title", "" )}

                            <div className="text-sm font-normal text-t3 flex-[0_0_auto] whitespace-nowrap">
                                <CricketMatchRecordDisplay
                                    matchRecord={get( eventInfo, "matchups.0.matchRecord", "" )}
                                    showCompactInnings={isCricketEvent}
                                />
                            </div>
                        </span>
                    </div>
                    <span
                        className={cn( "text-base font-[500] text-t1", isEnded && "opacity-40" )}
                        onClick={( e ) => {
                            e.stopPropagation();
                            onToggleYes();
                        }}
                    >
                        {yesDisplayText}
                    </span>
                </div>

                <div className="flex items-center gap-2 h-8">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                        {get( eventInfo, "matchups.1.matchupImageUrl", "" ) && (
                            <SmartImage
                                src={get( eventInfo, "matchups.1.matchupImageUrl", "" )}
                                alt=""
                                className="size-8 rounded-lg object-cover flex-shrink-0"
                                imageUrlOptions={{ width: 150, height: 150 }}
                            />
                        )}
                        <span className="text-sm font-medium text-t1 truncate flex-1 min-w-0 flex items-center gap-1">
                            {get( eventInfo, "matchups.1.title", "" )}

                            <div className="text-sm font-normal text-t3 flex-[0_0_auto] whitespace-nowrap">
                                <CricketMatchRecordDisplay
                                    matchRecord={get( eventInfo, "matchups.1.matchRecord", "" )}
                                    showCompactInnings={isCricketEvent}
                                />
                            </div>
                        </span>
                    </div>
                    <span
                        className={cn( "text-base font-[500] text-t1", isEnded && "opacity-40" )}
                        onClick={( e ) => {
                            e.stopPropagation();
                            onToggleNo();
                        }}
                    >
                        {noDisplayText}
                    </span>
                </div>
            </div>
        </>
    );
}

interface GuessSummarySectionProps {
    guess: any;
    showAggregatePredictions: boolean;
}

// 竞猜摘要区：单竞猜展示竞猜标题，聚合竞猜展示分类标题，主竞猜不重复展示标题。
function GuessSummarySection( { guess, showAggregatePredictions }: GuessSummarySectionProps ) {
    const shouldShowSummary = showAggregatePredictions || Number( get( guess, "mainFlag", 0 ) ) !== 1;
    if ( shouldShowSummary ) {
        const title = showAggregatePredictions
            ? ( get( guess, "groupTitle", "" ) || get( guess, "title", "" ) )
            : get( guess, "title", "" );
        return (
            <div className="h-5 flex items-center gap-6 mb-2 text-t1 text-sm font-[500]">
                <div className="text-ellipsis overflow-hidden whitespace-nowrap flex-1 min-w-0">{title}</div>
                <div className="text-base font-[600]">{getOptionPercentText( guess, "yesRealtimePrice" )}</div>
            </div>
        );
    }

    return null;
}

/**
 * 赛事简化卡片
 * 用于赛事在非赛事分类（ifSport !== true）下的展示
 */
export const SportSimpleEventCard = forwardRef<SportSimpleEventCardRef, SportSimpleEventCardProps>(
    function SportSimpleEventCard( { eventInfo, className, imageSize = "default", orderType, onCardClick }, ref ) {
        const router = useAppRouter();
        const pathname = usePathname();
        const { open } = useTradePanel();
        const [ selectedGuessIndex, setSelectedGuessIndex ] = useState( 0 );
        const [ showYesOdds, setShowYesOdds ] = useState( false );
        const [ showNoOdds, setShowNoOdds ] = useState( false );

        // 使用 DOM 收集 Hook
        const { registerButton, collectDOMInfo } = useCollectDOMInfo();

        // 暴露 DOM 收集方法给父组件
        useImperativeHandle( ref, () => ( { collectDOMInfo } ) );

        const {
            guess,
            aggregateGuesses,
            aggregateSelectedIndex,
            showAggregatePredictions,
            showMatchupSection,
            isPromotedEvent,
            shouldRender,
        } = useMemo( () => resolveSportEventCardState( eventInfo, selectedGuessIndex ), [
            eventInfo,
            selectedGuessIndex,
        ] );

        if ( !shouldRender ) return null;

        const isEnded = isEventExpired( eventInfo ) || isGuessExpired( guess );
        const yesPercentText = getOptionPercentText( guess, "yesRealtimePrice" );
        const noPercentText = getOptionPercentText( guess, "noRealtimePrice" );
        const yesDisplayText = yesPercentText;
        const noDisplayText = noPercentText;
        const sportIcon = getSportCardIcon( eventInfo );

        const handleCardClick = () => {
            if ( skipRoutes.includes( pathname ) ) {
                return;
            }
            if ( onCardClick ) {
                onCardClick();
            } else {
                const eventNo = get( eventInfo, "eventNo", "" );
                const eventCategory = get( eventInfo, "eventCategory", undefined );
                const guessNo = !isPromotedEvent ? get( guess, "guessNo", "" ) : undefined;
                void navigateToEventDetail( router, {
                    eventNo,
                    isMainPageEvent: true,
                    ...( guessNo && { guessNo } ),
                    ...( eventCategory !== undefined && { eventCategory } ),
                } );
            }
        };

        const handleButtonClick = ( e: React.MouseEvent, isYes: boolean ) => {
            e.stopPropagation();
            open( {
                guessInfo: guess,
                eventInfo,
                detailInfo: eventInfo,
                selectedOption: isYes ? "yes" : "no",
                tradeType: "buy",
                pathname,
                ...( orderType ? { orderType } : {} ),
            } );
        };
        return (
            <div
                className={cn( "p-3 bg-surface-card rounded-xl relative", shadowCls, className )}
                onClick={handleCardClick}
            >
                {!isPromotedEvent && (
                    <div className="h-7 mb-1 flex items-center gap-1 text-t1 text-sm font-[500] rounded">
                        <SmartImage src={sportIcon} alt="" className="size-6 shrink-0" />
                        {eventInfo.title}
                    </div>
                )}

                {isPromotedEvent && showMatchupSection && (
                    <PromotedEventSection
                        eventInfo={eventInfo}
                        imageSize={imageSize}
                        isEnded={isEnded}
                        yesDisplayText={yesDisplayText}
                        noDisplayText={noDisplayText}
                        onToggleYes={() => setShowYesOdds( ( prev ) => !prev )}
                        onToggleNo={() => setShowNoOdds( ( prev ) => !prev )}
                    />
                )}

                <GuessSummarySection guess={guess} showAggregatePredictions={showAggregatePredictions} />
                {showAggregatePredictions && (
                    <div onClick={( e ) => e.stopPropagation()}>
                        <PredictionCardContainer
                            sliderValues={aggregateGuesses}
                            selectedIndex={aggregateSelectedIndex}
                            onSliderChange={setSelectedGuessIndex}
                        />
                    </div>
                )}

                {!isEnded && (
                    <div className="flex gap-1.5 mb-3" onClick={( e ) => e.stopPropagation()}>
                        <button
                            ref={( el ) => registerButton( "yes-button", el )}
                            onClick={( e ) => handleButtonClick( e, true )}
                            className={cn(
                                "relative flex-1 h-10 rounded text-sm font-[500] active:scale-95 transition-transform flex items-center gap-1 justify-center",
                                yesBtnCls,
                            )}
                        >
                            <div className="text-sm truncate max-w-[85px]">
                                {getOptionLabel( "yes", guess, eventInfo )}
                            </div>
                            <div>{formatUnitPrice( getOptionPrice( guess, "yesRealtimePrice" ) )}</div>
                        </button>
                        <button
                            ref={( el ) => registerButton( "no-button", el )}
                            onClick={( e ) => handleButtonClick( e, false )}
                            className={cn(
                                "relative flex-1 h-10 rounded text-sm font-[500] active:scale-95 transition-transform flex items-center gap-1 justify-center",
                                noBtnCls,
                            )}
                        >
                            <div className="text-sm truncate max-w-[85px]">
                                {getOptionLabel( "no", guess, eventInfo )}
                            </div>
                            <div>{formatUnitPrice( getOptionPrice( guess, "noRealtimePrice" ) )}</div>
                        </button>
                    </div>
                )}

                {/* 第四部分：交易量 + 倒计时 */}
                <VolRect eventInfo={eventInfo} showLiveLabel={false} />
            </div>
        );
    },
);
